// Shape the HFP v3 report model (hfpV3ReportAdapter output, with the
// rmsGenerateHfpV3Report narratives merged in) into the display model the
// redesigned service-user report renders. Pure: no fetching, no globals.
//
// Tom's hard rules are structural here: home and school are carried as two
// separate values on every area and are never combined, and nothing in the
// output is a number or a percentage.
import {
  SECTIONS,
  areaDisplay,
  levelFromFamilyWord,
  levelFromBucket,
  normaliseAreaName,
  pronounForms,
  numberWord,
} from './hfpDisplay';

function functionalAreas(model, internalDomain) {
  const domainData =
    internalDomain === 'Global Participation Indicators'
      ? (model.gpi_areas || {})[internalDomain]
      : (model.areas_of_development || {})[internalDomain];
  const rows = (domainData || {}).rows || [];
  return rows.map((row) => {
    // Match the stable internal key first; the crosswalk suName is only a
    // fallback so a crosswalk copy edit cannot re-order or rename an area.
    let resolved = areaDisplay(internalDomain, row.subdomain);
    if (resolved.unmatched) resolved = areaDisplay(internalDomain, row.subdomain_plain);
    return {
      id: row.subdomain,
      name: resolved.name,
      order: resolved.order,
      home: levelFromFamilyWord(row.home_label),
      school: levelFromFamilyWord(row.school_label),
      story: row.llm_paragraph || null,
      storyError: Boolean(row.llm_error),
      help: { home: null, school: null },
    };
  });
}

function environmentalAreas(model, narrativesArrived) {
  const subs = ((model.environmental_summary || {}).subdomains) || [];
  return subs.map((sub) => {
    const { name, order } = areaDisplay('Environmental Factors', sub.name);
    return {
      id: sub.name,
      name,
      order,
      home: levelFromFamilyWord(sub.home_label),
      school: levelFromFamilyWord(sub.school_label),
      // The adapter fills environmental stories with a deterministic
      // placeholder sentence until the narratives payload arrives; hold the
      // story back so these cards show the loading skeleton like every other
      // section until then.
      story: narrativesArrived ? sub.narrative || null : null,
      storyError: false,
      help: { home: null, school: null },
    };
  });
}

function helpGroups(model, setting) {
  const groups = (((model.suggested_adjustments || {})[setting] || {}).groups) || [];
  return groups.map((group) => ({
    // The v3 matcher carries the stable internal subdomain; older cached
    // payloads may predate it, so the display name remains the fallback join.
    internalId: group.internal_subdomain || null,
    areaName: normaliseAreaName(group.subdomain),
    level: levelFromBucket(group.bucket),
    whyMatters: group.why_matters_text || null,
    impactBullets: group.impact_bullets || [],
    adjustments: (group.adjustments || []).map((item, i) => ({
      number: i + 1,
      title: item.short_label || item.text || '',
      category: item.type_label || null,
      body: item.framing_sentence || (item.short_label ? item.text : '') || '',
      bullets: item.implementation_bullets || [],
    })),
  }));
}

/**
 * @param {object} model  hfpV3ReportAdapter output (narratives merged when ready)
 * @param {string} childName
 * @param {string} pronounSet  'he/him' | 'she/her' | 'they/them' | 'ze/zir'
 * @param {object} [options]
 * @param {boolean} [options.narrativesArrived]  the narratives callable has
 *   returned a payload (regardless of which optional blocks it carries)
 */
export default function buildHfpDisplayModel(
  model,
  childName,
  pronounSet = 'they/them',
  { narrativesArrived = false } = {}
) {
  const name = childName || 'the child';
  const p = pronounForms(pronounSet);

  const sections = SECTIONS.map((section, index) => {
    const areas = (
      section.internal === 'Environmental Factors'
        ? environmentalAreas(model, narrativesArrived)
        : functionalAreas(model, section.internal)
    ).sort((a, b) => a.order - b.order);
    return {
      key: section.key,
      internal: section.internal,
      title: section.title,
      lede: section.lede({ name, p, areaCount: areas.length }),
      eyebrow: `Section ${index + 1} of ${SECTIONS.length} · ${areas.length} area${areas.length === 1 ? '' : 's'}`,
      areas,
    };
  });

  // Help lists: one group per area per setting, exactly as the matcher emits
  // them (list = areas with written adjustments for that setting, grouped by
  // that setting's level, priority areas first, in the matcher's rank order).
  const adjustmentsReady = Boolean(model.suggested_adjustments);
  const help = {};
  ['home', 'school'].forEach((setting) => {
    const groups = helpGroups(model, setting);
    help[setting] = {
      priority: groups.filter((g) => g.level === 'priority'),
      support: groups.filter((g) => g.level === 'support'),
      all: groups,
    };
  });

  // Attach each setting's group to its profile area (stable internal id
  // first, display name as the fallback) so render code never string-matches.
  const allAreas = sections.flatMap((s) => s.areas);
  ['home', 'school'].forEach((setting) => {
    help[setting].all.forEach((group) => {
      const area =
        (group.internalId && allAreas.find((a) => a.id === group.internalId)) ||
        allAreas.find((a) => a.name === group.areaName);
      if (area) area.help[setting] = group;
    });
  });

  const helpLede = (setting) => {
    const count = help[setting].all.length;
    return (
      `${count === 0 ? 'No' : numberWord(count).replace(/^./, (c) => c.toUpperCase())} ` +
      `area${count === 1 ? '' : 's'}, priority areas first. Ideas for ${setting}, based on what ${setting} said.`
    );
  };

  return {
    childName: name,
    pronouns: p,
    sections,
    help,
    helpLedes: { home: helpLede('home'), school: helpLede('school') },
    narrativesArrived,
    adjustmentsReady,
    // Clinical-safety carriage: the pain override drives a FIXED note on the
    // Sensory section (rendered verbatim, never model-generated) and the
    // calibration line rides with the legal notice — neither may be dropped.
    painSafety: Boolean(model._pain_safety),
    calibrationNote: model._calibration_note || null,
  };
}
